'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useState } from 'react'

// walrus
import Box from '@components/kit/box'
import Card from '@components/kit/card'
import Text from '@components/kit/text'
import Loading from '@components/kit/loading'
import Button from '@components/kit/button'
import Modal from '@components/kit/modal'
import { Row } from '@components/kit/grid'

// lib
import { ChatContext } from '../../chat.web'
import { useHealthTrackingStore } from '@store/HealthTracking'
import { useSkillsStore } from '@store/Skills'
import { ISkillsResponse } from '@store/Skills/types'
import {
  GLP1Onboarding,
  GLP1PrescriberInfo,
  IProgramOverview,
  IUserProgramStatus
} from '@store/HealthTracking/types'
import { SwitchInputFormated } from '@components/formFields'

// sessions
import CurrentOverview from './sessions/program-overview'
import PrescriberInfo from './sessions/prescriber-info'
import SkillBuilder from './sessions/skill-builder'
import GLP1Meds from './sessions/glp1-meds'

/* -----------------------------------------------------------------------------
 * GLP1Card
 * -------------------------------------------------------------------------- */

function GLP1Card() {
  const { userId } = useContext(ChatContext)
  const {
    fetchProgramOverview,
    fetchGLP1Prescriber,
    approveProgramOverview,
    fetchGLP1Onboarding
  } = useHealthTrackingStore.getState()
  const { fetchSkills } = useSkillsStore.getState()

  const [overview, setOverview] = useState<IProgramOverview | null>(null)
  const [isOverviewLoading, setIsOverviewLoading] = useState(true)

  const [prescriber, setPrescriber] = useState<GLP1PrescriberInfo | null>(null)
  const [isPrescriberLoading, setIsPrescriberLoading] = useState(true)

  const [skills, setSkills] = useState<ISkillsResponse | null>(null)
  const [isSkillsLoading, setIsSkillsLoading] = useState(true)

  const [onboarding, setOnboarding] = useState<GLP1Onboarding | null>(null)
  const [isOnboardingLoading, setIsOnboardingLoading] = useState(true)

  const [isDeprescribeModalOpen, setIsDeprescribeModalOpen] = useState(false)
  const [isDeprescribing, setIsDeprescribing] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    if (!userId) return

    setIsOverviewLoading(true)
    setIsPrescriberLoading(true)
    setIsSkillsLoading(true)
    setIsOnboardingLoading(true)

    fetchProgramOverview(userId).then(data => {
      setOverview(data)
      setIsOverviewLoading(false)
    })

    fetchGLP1Prescriber(userId).then(data => {
      setPrescriber(data)
      setIsPrescriberLoading(false)
    })

    fetchSkills(userId).then(data => {
      setSkills(data)
      setIsSkillsLoading(false)
    })

    fetchGLP1Onboarding(userId).then(data => {
      setOnboarding(data)
      setIsOnboardingLoading(false)
    })
  }, [userId])

  const isLoading =
    isOverviewLoading ||
    isPrescriberLoading ||
    isSkillsLoading ||
    isOnboardingLoading

  const isApproved =
    overview?.programs?.some(p => p.status === 'APPROVED') ?? false
  const nextStatus: IUserProgramStatus = isApproved ? 'REJECTED' : 'APPROVED'

  const handleDeprescribe = async () => {
    if (!overview?.programs?.length) return
    setIsDeprescribing(true)
    for (const program of overview.programs) {
      await approveProgramOverview(userId, program.id, nextStatus)
    }
    setIsDeprescribing(false)
    setIsDeprescribeModalOpen(false)
    setIsRefreshing(true)
    const refreshed = await fetchProgramOverview(userId)
    setOverview(refreshed)
    setIsRefreshing(false)
  }

  if (isLoading) {
    return (
      <React.Fragment>
        <Row mt='24'>
          <Text ml='12' font='display.16.bold'>
            GLP-1
          </Text>
        </Row>
        <Box
          mt='08'
          width='100%'
          height={200}
          justifyContent='center'
          alignItems='center'
        >
          <Loading />
        </Box>
      </React.Fragment>
    )
  }

  if (overview?.isGLP1 === false) {
    return (
      <React.Fragment>
        <Row mt='24' alignItems='center'>
          <Text ml='12' font='display.16.bold'>
            GLP-1
          </Text>
          <Text
            ml='04'
            font='text.16.medium'
            textColor='walrusNeutral700'
            textAlign='center'
          >
            (program unavailable for this member)
          </Text>
        </Row>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Row mt='24'>
        <Text ml='12' font='display.16.bold'>
          GLP-1
        </Text>
      </Row>

      <Box mt='08' flexDirection='row' gap='04'>
        <SwitchInputFormated
          checked={isApproved}
          onChange={() => setIsDeprescribeModalOpen(true)}
          label={isApproved ? 'Approved' : 'Not approved'}
          labelPosition='right'
        />
      </Box>

      <Box position='relative'>
        <Card mt='08' px='00' py='00'>
          <CurrentOverview overview={overview} />
          <PrescriberInfo prescriber={prescriber} />
          <GLP1Meds onboarding={onboarding} />
          <SkillBuilder skills={skills} />
        </Card>
        {isRefreshing && (
          <Box
            position='absolute'
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg='white'
            justifyContent='center'
            alignItems='center'
          >
            <Loading />
          </Box>
        )}
      </Box>

      <Modal
        open={isDeprescribeModalOpen}
        onRequestClose={() => setIsDeprescribeModalOpen(false)}
        showCloseButton={false}
        maxWidth={411}
        borderRadius='medium'
      >
        <Text variant='h3' mb='20'>
          {isApproved
            ? 'Are you sure you want to deprescribe this member?'
            : 'Are you sure you want to prescribe this member?'}
        </Text>

        <Box width='100%' flexDirection='row' justifyContent='center'>
          <Button
            onPress={() => setIsDeprescribeModalOpen(false)}
            variant='punchThroughViolet'
            borderColor='shade03'
            borderWidth={1}
          >
            Cancel
          </Button>
          <Button onPress={handleDeprescribe} width={75} height={52} ml='12'>
            {isDeprescribing ? <Loading fill='white' size='small' /> : 'Yes'}
          </Button>
        </Box>
      </Modal>
    </React.Fragment>
  )
}

export default GLP1Card
